// src/oura/components/search/TrafficCard.tsx
//
// Sessions from Google Analytics 4, over time: the Traffic card that used to
// sit on the staff Overview, moved here whole (sessions, the change, the Day /
// Week / Month series, the four connection states and the traffic refresh).
//
// Traffic exists ONLY once the client's GA4 is connected. The backend omits
// the block entirely otherwise and never fabricates it (`_traffic()` in
// app/dependencies/aeo_overview.py), so `traffic` absent is the tell. Showing
// 0 visits over a flat line read as "this site gets no traffic", which is a
// different and wrong claim. The source is GA4: Search Console cannot produce
// sessions at all.
//
// THREE DIFFERENT DATE RANGES. The total covers `window` (the last 28 days).
// Each series covers its own span (`ga4_windows` in aeo_connector_service.py):
// the days are the window itself, the weeks are 12 whole ISO weeks ending on
// the last Sunday, and the months are 12 calendar months whose newest one is
// the month so far. So the chart is dated by its own points, never by the
// window: 12 weeks under a 28-day caption was a wrong date on every week.
// Both dates are on the card, so each says what it dates: the head names the
// chart's span by its readings ("12 weeks, 22 Jun to 13 Sep"), the total
// names its window ("in the last 28 days, 19 Aug to 15 Sep"). Two bare date
// ranges a few pixels apart read as one card disagreeing with itself.

import { useState } from "react";
import { CalendarIcon, ActivityIcon, PlugIcon } from "lucide-react";
import type { BusinessAudit } from "../../audit";
import type { DataConnector } from "../../connectors";
import type { View } from "../../nav";
import { cn } from "../../lib/cn";
import { shortDate, shortMonth } from "../../lib/oura";
import { MiniBars } from "../../primitives/MiniBars";
import { useStageRefresh } from "../../hooks/useRefreshJob";
import { CardRefresh, RefreshProgress } from "../RefreshControl";
import { STAGE_RERUN_LIVE, TRAFFIC_REFRESH } from "../../refreshJobs";
import { ArrowLink, FoldCard, PillButton, Seg, TYPE } from "@oura/ui";
import { PctChange } from "./PctChange";
import { weekIndexOf } from "./format";
import { TrafficMarkers } from "./TrafficMarkers";

type Traffic = NonNullable<BusinessAudit["traffic"]>;
type Range = "day" | "week" | "month";

const RANGE_LABEL: Record<Range, string> = { day: "Day", week: "Week", month: "Month" };
const RANGE_NOUN: Record<Range, string> = { day: "daily", week: "weekly", month: "monthly" };
const RANGE_UNIT: Record<Range, [string, string]> = { day: ["day", "days"], week: ["week", "weeks"], month: ["month", "months"] };
/** Where a missing range sends the reader: the week first, the shape the card was built around. */
const FALLBACK_ORDER: Range[] = ["week", "day", "month"];
/** Up to this many readings are drawn as bars, each with its value over it. */
const BAR_LIMIT = 6;

/**
 * One reading as GA4 dated it: a day's date, or a week's or a month's first
 * day. `start` is absent only on the weekly `trend` of a run stored before the
 * dated series existed (ourteam-backend#5018).
 */
interface Bucket {
    start?: string;
    sessions: number;
}

/** Each range's readings, as sent. A range GA4 did not send is empty, never derived from another. */
function bucketsOf(T: Traffic | undefined): Record<Range, Bucket[]> {
    return {
        day: T?.series?.daily?.map((p) => ({ start: p.date, sessions: p.sessions })) ?? [],
        week: T?.series?.weekly ?? T?.trend.map((sessions) => ({ sessions })) ?? [],
        month: T?.series?.monthly ?? [],
    };
}

/** An ISO date `days` after another, in UTC so the viewer's time zone cannot shift it. */
function isoDaysAfter(iso: string, days: number): string {
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * The dates the chart on screen covers, from its own first reading to its
 * last: a week runs to its Sunday, a month is named rather than dated. Null
 * when the range has no readings, so an empty chart never carries a span.
 *
 * The one estimate is the undated weekly trend: it is placed as that many
 * whole weeks ending on the scan date, which is how it read before the
 * series carried dates.
 */
function spanOf(range: Range, buckets: Bucket[], capturedAt?: string | null): { from: string; to: string } | null {
    if (buckets.length === 0) return null;
    const first = buckets[0].start;
    const last = buckets[buckets.length - 1].start;
    if (first && last) {
        if (range === "month") return { from: shortMonth(first, true), to: shortMonth(last, true) };
        return { from: shortDate(first), to: shortDate(range === "week" ? isoDaysAfter(last, 6) : last) };
    }
    if (range !== "week" || !capturedAt) return null;
    const end = capturedAt.slice(0, 10);
    return { from: shortDate(isoDaysAfter(end, -(7 * buckets.length - 1))), to: shortDate(end) };
}

/**
 * Whether the newest month is the month so far. The backend ends the monthly
 * series where the data ends, deliberately mid-month (`ga4_windows`), and says
 * the frontend is what names it partial. Only a window that ends on a
 * month's last day makes the newest month whole.
 */
function newestMonthPartial(window: Traffic["window"]): boolean {
    if (!window) return true;
    return isoDaysAfter(window.end, 1).slice(8, 10) !== "01";
}

function Sparkline({ points, className }: { points: number[]; className?: string }) {
    const w = 300;
    const hgt = 96;
    const pad = 6;
    // From zero, like the bars and the scale beside the chart: scaled to its
    // own minimum, a line that moved from 40 to 44 climbed the whole card
    // above a "0" it never came near.
    const max = Math.max(1, ...points);
    // Horizontal padding as well as vertical: the end marker used to be drawn
    // ON the edge of the viewBox, so the browser clipped half of it and a
    // single week of traffic rendered as half a dot in the bottom-left corner.
    const padX = 8;
    const xy = points.map((p, i) => ({
        // One point has no span to spread across: centre it rather than
        // dividing by zero and drawing nothing at all.
        x: points.length === 1 ? w / 2 : padX + (i / (points.length - 1)) * (w - padX * 2),
        y: hgt - pad - (p / max) * (hgt - pad * 2),
    }));
    const line = xy.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
    const last = xy[xy.length - 1];
    const gridY = [0.25, 0.5, 0.75].map((f) => pad + f * (hgt - pad * 2));
    return (
        <svg aria-hidden viewBox={`0 0 ${w} ${hgt}`} className={cn("w-full", className)} preserveAspectRatio="none">
            {gridY.map((y) => (
                <line key={y} x1="0" y1={y} x2={w} y2={y} stroke="#f1f5f9" strokeWidth="1" />
            ))}
            <polygon points={`${xy[0].x.toFixed(1)},${hgt} ${line} ${last.x.toFixed(1)},${hgt}`} fill="#0B49FE" opacity="0.07" />
            {points.length > 1 && (
                <polyline points={line} fill="none" stroke="#0B49FE" strokeWidth="2.5" strokeLinejoin="round" />
            )}
            <circle cx={last.x} cy={last.y} r="3.5" fill="#0B49FE" stroke="#fff" strokeWidth="1.5" />
        </svg>
    );
}

/** What the card says when there is no traffic block, by connector state. */
function NoTraffic({
    ga4Connector,
    empty = null,
    isStaff,
    onNavigate,
}: {
    ga4Connector?: DataConnector | null;
    empty?: BusinessAudit["trafficEmpty"];
    isStaff?: boolean;
    onNavigate?: (v: View) => void;
}) {
    const status = ga4Connector?.status;
    // Measured, no sessions (ourteam-backend#6749): the dates GA4 was read for. Only while the
    // connector reads `connected` now: an action_needed or not-yet-loaded connector keeps its own words (Jack).
    const confirmed = status === "connected" ? empty : null;
    const span = confirmed ? `${shortDate(confirmed.start)} to ${shortDate(confirmed.end)}` : null;
    if (!isStaff) {
        // The client's own words: no tool names, no "the client's site". The
        // staff detail names a property, which means nothing to an owner.
        return (
            <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
                <ActivityIcon aria-hidden size={20} className="text-gray-300" />
                {/* Connected with no traffic block is unknown, not zero: the
                    scan may simply carry no figures (Jack's review of #4211). */}
                <p className={cn(TYPE.tb, "m-0 font-medium text-gray-600")}>
                    {span ? "No visits recorded" : status === "connected" ? "No visit figures for this scan" : "Visits not connected yet"}
                </p>
                <p className={cn(TYPE.tm, "m-0 max-w-[280px] text-gray-400")}>
                    {span
                        ? `Your site's visit counter is connected, but it recorded no visits from ${span}.`
                        : status === "connected"
                        ? "Your site's visit counter is connected, but this scan has no visit figures."
                        : status === "action_needed"
                            ? "Your site's visit counter stopped sending figures."
                            : "Your visits show here once your site's visit counter is connected."}
                </p>
                {onNavigate && (
                    <PillButton size="sm" icon={PlugIcon} className="mt-1" onClick={() => onNavigate("connectors")}>
                        {status === "connected" || status === "action_needed" ? "Check connection" : "Connect"}
                    </PillButton>
                )}
            </div>
        );
    }
    return (
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
            <ActivityIcon aria-hidden size={20} className="text-gray-300" />
            {status === "connected" ? (
                <>
                    {/* Connected, and GA4 answered with no sessions. Not "not
                        connected": the Connectors page says otherwise, and a
                        Connect button would reconnect the same property. The
                        detail names that property, because the likeliest
                        reason is that it is not this client's
                        (ourteam-backend#4947). */}
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-600")}>
                        {span ? `No sessions recorded, ${span}` : "No sessions in the last 28 days"}
                    </p>
                    <p className={cn(TYPE.tm, "m-0 max-w-[280px] text-gray-400")}>
                        Google Analytics is connected but returned no sessions. Check that the connected property is
                        this client&rsquo;s site.
                    </p>
                    {ga4Connector?.detail && <p className={cn(TYPE.tm, "m-0 max-w-[280px] text-gray-500")}>{ga4Connector.detail}</p>}
                </>
            ) : status === "action_needed" ? (
                <>
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-600")}>Google Analytics needs attention</p>
                    <p className={cn(TYPE.tm, "m-0 max-w-[280px] text-gray-400")}>
                        {ga4Connector?.detail || "The connection stopped returning data."}
                    </p>
                </>
            ) : (
                <>
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-600")}>Traffic not connected</p>
                    <p className={cn(TYPE.tm, "m-0 max-w-[260px] text-gray-400")}>
                        Connect the client&rsquo;s Google Analytics 4 to pull real sessions, visitors and engagement.
                    </p>
                </>
            )}
            {onNavigate && (
                <PillButton size="sm" icon={PlugIcon} className="mt-1" onClick={() => onNavigate("connectors")}>
                    {status === "connected" ? "Check connection" : status === "action_needed" ? "Fix connection" : "Connect Google Analytics"}
                </PillButton>
            )}
        </div>
    );
}

/** The chart for one range: labelled month bars, a few bars, or a line with its dates underneath. */
function SessionsChart({
    range,
    buckets,
    span,
    partialMonth,
    markerDates = [],
}: {
    range: Range;
    buckets: Bucket[];
    span: { from: string; to: string } | null;
    partialMonth: boolean;
    /** Days to draw a dashed line on, on the weekly line only. */
    markerDates?: string[];
}) {
    const points = buckets.map((b) => b.sessions);
    const months = range === "month";
    const bars = months || points.length <= BAR_LIMIT;
    // Month bars carry their own names. Twelve of them do not fit on a phone,
    // so there every other name steps aside, counted back from the newest so
    // the month so far always keeps its label.
    const labels = months
        ? buckets.map((b, i) => {
            const newest = i === buckets.length - 1;
            const stepAside = points.length > BAR_LIMIT && (buckets.length - 1 - i) % 2 === 1;
            return (
                <span className={cn("block", stepAside && "invisible sm:visible")}>
                    {b.start ? shortMonth(b.start) : ""}
                    {newest && partialMonth && <span className="block text-gray-500">so far</span>}
                </span>
            );
        })
        : undefined;
    return (
        <div>
            <div className={cn("flex gap-2", months ? "h-48" : "h-40")}>
                {/* The scale, on the left. Without it the chart said only "it
                    went up a bit": one dot in the middle of an empty box, with
                    nothing to read it against. Bars and line both start at 0. */}
                <div className={cn("flex w-8 shrink-0 flex-col justify-between py-0.5 text-right text-[11px] leading-none tabular-nums text-gray-400", months && "pb-8")}>
                    <span>{Math.max(...points).toLocaleString("en-GB")}</span>
                    <span>0</span>
                </div>
                {/* relative + absolute: a viewBox svg's intrinsic ratio can
                    force a flex parent far taller than intended. */}
                <div className="relative min-w-0 flex-1">
                    {bars ? (
                        <MiniBars points={points} labels={labels} showValues={points.length <= BAR_LIMIT} className="absolute inset-0" />
                    ) : (
                        <Sparkline points={points} className="absolute inset-0 h-full" />
                    )}
                    {/* Same x as the Sparkline's points: 8 of 300 viewBox units padded each side. */}
                    {!bars && range === "week" && points.length > 1 && Array.from(new Set(markerDates.map((d) => weekIndexOf(buckets, d))))
                        .filter((i) => i >= 0)
                        .map((i) => (
                            <span
                                key={i}
                                aria-hidden
                                className="absolute inset-y-0 border-l border-dashed border-amber-500"
                                style={{ left: `${((8 + (i / (points.length - 1)) * 284) / 300) * 100}%` }}
                            />
                        ))}
                </div>
            </div>
            {/* And the span, underneath, for a chart whose readings are not
                named one by one. A chart with no axis at all asks a reader to
                go looking for its dates. */}
            {!months && span && (
                <div className="mt-1 flex justify-between pl-10 text-[11px] text-gray-400">
                    <span>{span.from}</span>
                    <span>{span.to}</span>
                </div>
            )}
        </div>
    );
}

export function TrafficCard({
    traffic,
    empty = null,
    ga4Connector,
    capturedAt,
    assessmentId,
    businessId,
    isStaff,
    onRefreshed,
    onNavigate,
}: {
    traffic?: Traffic;
    /**
     * GA4 was read and recorded NO sessions in this window (ourteam-backend#6749).
     * Measured, so the card says so with the dates, instead of "no figures".
     * Null when not known (an older payload, or `traffic` is present).
     */
    empty?: BusinessAudit["trafficEmpty"];
    /**
     * The business's GA4 connector, so an absent `traffic` block can say WHY.
     *
     * `traffic` is absent both when GA4 is not connected and when it is
     * connected but returned no sessions for the last 28 days, including when
     * the BE bound it to another site's property (ourteam-backend#4947).
     * `undefined` = not known yet (fixture business, or the connector list has
     * not arrived) and the card keeps its plain not-connected state; `null` =
     * the list arrived without a GA4 entry.
     */
    ga4Connector?: DataConnector | null;
    /** The scan's capture date: where an undated weekly trend is placed. */
    capturedAt?: string | null;
    assessmentId?: string;
    businessId?: string;
    isStaff?: boolean;
    onRefreshed?: () => void | Promise<void>;
    onNavigate?: (v: View) => void;
}) {
    /** Google Analytics only (#3906). */
    const job = useStageRefresh({
        spec: TRAFFIC_REFRESH,
        assessmentId,
        businessId: businessId ?? "none",
        onSettled: () => onRefreshed?.(),
    });
    const T = traffic;
    const ga4Status = ga4Connector?.status;
    // The control sits in EVERY connected state, including "connected but no
    // sessions": that is precisely the state someone wants to re-check. Not
    // connected keeps Connect as its only action (#3906).
    const canRefresh = STAGE_RERUN_LIVE && !!isStaff && !!assessmentId && !job.absent && ga4Status !== "not_connected";
    /**
     * Which granularities this scan actually has, and the one on screen.
     *
     * The buttons answer from the payload: a granularity GA4 did not send is
     * still offered, and the chart says it is missing, instead of being
     * answered by arithmetic on another series.
     */
    const buckets = bucketsOf(T);
    const fallback = FALLBACK_ORDER.find((r) => buckets[r].length > 0) ?? null;
    const hasSeries = fallback !== null;
    const [range, setRange] = useState<Range>("week");
    // Selecting a range this scan has no series for is allowed: the chart says
    // what is missing. A disabled button explains itself only on hover, which
    // is no explanation at all on a touch screen.
    const shown = buckets[range];
    const span = spanOf(range, shown, capturedAt);
    // The total GA4 measured over the window GA4 measured it in, whichever
    // granularity is on screen. Never a division: "visits today" from a
    // 28-day total is an estimate wearing the clothes of a reading.
    const windowDays = T?.window?.days ?? 28;

    return (
        <FoldCard
            id="traffic"
            anchor="traffic"
            title="Traffic"
            icon={{ icon: ActivityIcon, area: "google" }}
            foldedFact={T ? `${T.visits.toLocaleString("en-GB")} ${isStaff ? "sessions" : "visits"}` : undefined}
            right={(canRefresh || hasSeries) && (
                <>
                    {canRefresh && <CardRefresh job={job} spec={TRAFFIC_REFRESH} />}
                    {span && (
                        <span className="hidden items-center gap-1 whitespace-nowrap sm:inline-flex">
                            <CalendarIcon aria-hidden size={14} className="text-gray-400" />
                            {shown.length} {RANGE_UNIT[range][shown.length === 1 ? 0 : 1]}, {span.from} to {span.to}
                        </span>
                    )}
                    {hasSeries && (
                        <Seg<Range>
                            size="sm"
                            ariaLabel="Sessions by"
                            value={range}
                            onChange={setRange}
                            items={(["day", "week", "month"] as const).map((r) => ({ key: r, label: RANGE_LABEL[r] }))}
                        />
                    )}
                </>
            )}
        >
            <p className={cn(TYPE.tc, "m-0 mb-3 text-gray-500")}>
                {isStaff ? "Sessions over time, from Google Analytics 4." : "Visits to your site from every source, over time."}
            </p>
            <RefreshProgress job={job} runningText={TRAFFIC_REFRESH.running} />
            {!T ? (
                <NoTraffic ga4Connector={ga4Connector} empty={empty} isStaff={isStaff} onNavigate={onNavigate} />
            ) : (
                <>
                    <div className="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className={cn(TYPE.num, "text-gray-900")}>{T.visits.toLocaleString("en-GB")}</span>
                        <span className={cn(TYPE.tc, "text-gray-400")}>
                            {isStaff ? "sessions" : "visits"} in the last {windowDays} days
                            {T.window && `, ${shortDate(T.window.start)} to ${shortDate(T.window.end)}`}
                        </span>
                        {T.deltaPct !== null && (
                            <span className={cn(TYPE.tc, "text-gray-500")}>
                                <PctChange percent={T.deltaPct} days={windowDays} />
                            </span>
                        )}
                    </div>
                    {fallback && shown.length === 0 ? (
                        // The click has to land somewhere. The measured total
                        // stays on screen above this; what is missing is the
                        // shape of the window at this granularity.
                        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center">
                            <p className={cn(TYPE.tb, "m-0 font-medium text-gray-500")}>
                                {RANGE_LABEL[range]} figures are not on this scan
                            </p>
                            {/* Not "Google Analytics returned no daily figures".
                                It gives whatever dimension it is asked for; a
                                scan stored before ourteam-backend#5018 asked for
                                weeks only. Blaming the source for our own
                                request is the kind of sentence that gets
                                repeated to a client. */}
                            <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-400")}>
                                {isStaff ? (
                                    <>
                                        This scan stored no {RANGE_NOUN[range]} figures. The same Google Analytics property can
                                        report them; the {RANGE_NOUN[fallback]} figures are on this scan.
                                    </>
                                ) : (
                                    <>
                                        There are no {RANGE_NOUN[range]} figures for this scan. The {RANGE_NOUN[fallback]} figures are.
                                    </>
                                )}
                            </p>
                            <ArrowLink onClick={() => setRange(fallback)} className="mt-3">
                                Show by {RANGE_LABEL[fallback].toLowerCase()}
                            </ArrowLink>
                        </div>
                    ) : hasSeries ? (
                        <>
                            <SessionsChart
                                range={range}
                                buckets={shown}
                                span={span}
                                partialMonth={newestMonthPartial(T.window)}
                                markerDates={T.markers?.map((m) => m.date)}
                            />
                            {T.markers && (
                                <TrafficMarkers
                                    markers={T.markers}
                                    weeks={buckets.week}
                                    businessId={businessId}
                                    isStaff={isStaff}
                                    onChanged={onRefreshed}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <p className={cn(TYPE.tm, "m-0 tabular-nums text-gray-500")}>
                                {T.unique.toLocaleString("en-GB")} visitors · {T.pagesPerVisit.toFixed(1)} pages per visit
                            </p>
                            <p className={cn(TYPE.tm, "m-0 mt-4 text-gray-400")}>
                                The week-by-week trend appears once {isStaff ? "Google Analytics reports it" : "your site reports it"}.
                            </p>
                        </>
                    )}
                </>
            )}
        </FoldCard>
    );
}
